import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Separator } from './ui/separator';
import { Checkbox } from './ui/checkbox';
import {
  Link2,
  CheckCircle,
  Loader2
} from 'lucide-react';
import salesService from '../services/salesService';
import purchaseService from '../services/purchaseService';

/**
 * LinkPODialog - 3-step wizard for linking existing unassigned POs to a Sales Order
 *
 * Step 1: Select unassigned POs (multi-select with client filtering)
 * Step 2: Map products and allocate quantities (supports partial allocation)
 * Step 3: Review and confirm mappings
 */
const LinkPODialog = ({ open, onClose, salesOrderId, salesOrderData, onLinked }) => {
  // Stepper states
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Data states
  const [unassignedPOs, setUnassignedPOs] = useState([]);
  const [selectedPOs, setSelectedPOs] = useState([]); // Array of PO objects
  const [productAllocations, setProductAllocations] = useState([]); // Array of allocation objects
  const [previewData, setPreviewData] = useState(null);

  const steps = ['选择采购单', '分配产品数量', '确认关联'];

  // Initialize: Fetch unassigned POs
  const fetchUnassignedPOs = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const clientId = salesOrderData?.client_ref;
      const filters = clientId ? { client_id: clientId } : {};

      const response = await purchaseService.getUnassignedPurchaseOrders(1, 100, filters);
      const poList = response?.purchase_orders || [];
      setUnassignedPOs(poList);
    } catch (err) {
      console.error('Error fetching unassigned POs:', err);
      setError('获取未关联采购单失败: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  }, [salesOrderData]);

  useEffect(() => {
    if (open) {
      fetchUnassignedPOs();
    }
  }, [open, fetchUnassignedPOs]);

  // Step 1: Handle PO selection
  const handlePOSelection = (po, isSelected) => {
    if (isSelected) {
      setSelectedPOs([...selectedPOs, po]);
    } else {
      setSelectedPOs(selectedPOs.filter(p => p._id !== po._id));
    }
  };

  const isAllSelected = unassignedPOs.length > 0 && selectedPOs.length === unassignedPOs.length;

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedPOs([]);
    } else {
      setSelectedPOs([...unassignedPOs]);
    }
  };

  // Step 2: Initialize product allocations when moving to step 2
  const initializeProductAllocations = () => {
    const soProducts = salesOrderData?.products || [];

    // Create allocation structure for each selected PO
    const allocations = selectedPOs.map(po => {
      const poProducts = po.products || [];

      // Find matching products between SO and PO
      const matchingProducts = soProducts.map(soProduct => {
        const soProductCode = soProduct.product_code || soProduct.product_id || soProduct.part_number;

        // Find matching product in PO
        const poProduct = poProducts.find(pp => {
          const poProductCode = pp.product_code || pp.product_id || pp.part_number;
          return poProductCode === soProductCode;
        });

        if (poProduct) {
          const soQty = parseFloat(soProduct.quantity || 0);
          const poQty = parseFloat(poProduct.quantity || 0);
          const poProductCode = poProduct.product_code || poProduct.product_id || poProduct.part_number;

          return {
            so_product_id: soProductCode,
            po_product_id: poProductCode,
            so_product_name: soProduct.name || '',
            so_required_qty: soQty,
            po_available_qty: poQty,
            allocated_qty: Math.min(soQty, poQty), // Default to min of SO and PO qty
            match: true,
          };
        }

        return null;
      }).filter(Boolean);

      return {
        po_id: po._id,
        po_number: po.purchase_order_id || po._id,
        supplier_name: po.supplier?.name || '未知供应商',
        allocations: matchingProducts,
      };
    }).filter(alloc => alloc.allocations.length > 0); // Only include POs with matching products

    setProductAllocations(allocations);
  };

  // Handle quantity change for a specific allocation
  const handleQuantityChange = (poIndex, productIndex, newQty) => {
    const updated = [...productAllocations];
    const allocation = updated[poIndex].allocations[productIndex];

    // Validate quantity
    const qty = parseFloat(newQty);
    if (isNaN(qty) || qty < 0) return;

    const maxQty = Math.min(allocation.so_required_qty, allocation.po_available_qty);
    allocation.allocated_qty = Math.min(qty, maxQty);

    setProductAllocations(updated);
  };

  // Step 3: Generate preview data
  const generatePreview = () => {
    const totalMappings = productAllocations.reduce(
      (sum, po) => sum + po.allocations.length,
      0
    );

    setPreviewData({
      total_pos: selectedPOs.length,
      total_mappings: totalMappings,
      details: productAllocations,
    });
  };

  // Handle step navigation
  const handleNext = () => {
    if (activeStep === 0) {
      if (selectedPOs.length === 0) {
        setError('请至少选择一个采购单');
        return;
      }
      initializeProductAllocations();
    } else if (activeStep === 1) {
      // Validate that at least one allocation exists
      const hasAllocations = productAllocations.some(po =>
        po.allocations.some(alloc => alloc.allocated_qty > 0)
      );

      if (!hasAllocations) {
        setError('请至少分配一个产品的数量');
        return;
      }

      generatePreview();
    }

    setError('');
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setError('');
    setActiveStep((prev) => prev - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setSelectedPOs([]);
    setProductAllocations([]);
    setPreviewData(null);
    setError('');
  };

  // Handle final submission
  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError('');

      // Format data for API
      const poAllocations = productAllocations
        .map(po => ({
          po_id: po.po_id,
          allocations: po.allocations
            .filter(alloc => alloc.allocated_qty > 0)
            .map(alloc => ({
              so_product_id: alloc.so_product_id,
              po_product_id: alloc.po_product_id,
              allocated_qty: alloc.allocated_qty,
            })),
        }))
        .filter(po => po.allocations.length > 0);

      const result = await salesService.linkPurchaseOrdersToSalesOrder(
        salesOrderId,
        poAllocations
      );

      // Success - notify parent and close
      if (onLinked) {
        onLinked(result);
      }

      handleClose();
    } catch (err) {
      console.error('Error linking POs to SO:', err);
      setError('关联采购单失败: ' + (err.response?.data?.error || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  // Stepper component
  const Stepper = () => (
    <div className="flex items-center justify-center mb-6">
      {steps.map((label, index) => (
        <React.Fragment key={label}>
          <div className="flex items-center">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                index < activeStep
                  ? 'bg-success/100 text-white'
                  : index === activeStep
                  ? 'bg-primary/100 text-white'
                  : 'bg-muted/80 text-muted-foreground'
              }`}
            >
              {index < activeStep ? <CheckCircle className="h-5 w-5" /> : index + 1}
            </div>
            <span
              className={`ml-2 text-sm ${
                index === activeStep ? 'font-medium text-primary' : 'text-muted-foreground'
              }`}
            >
              {label}
            </span>
          </div>
          {index < steps.length - 1 && (
            <div
              className={`w-16 h-0.5 mx-2 ${
                index < activeStep ? 'bg-success/100' : 'bg-muted/80'
              }`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  // Step 1: Select POs
  const renderStep1SelectPOs = () => (
    <div>
      <p className="text-sm text-muted-foreground mb-4">
        选择要关联到当前销售订单的未分配采购单
      </p>

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : unassignedPOs.length === 0 ? (
        <Alert>
          <AlertDescription>暂无未关联的采购单</AlertDescription>
        </Alert>
      ) : (
        <Card className="mt-4 max-h-[400px] overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted sticky top-0">
              <tr className="border-b">
                <th className="p-3 w-10">
                  <Checkbox
                    checked={isAllSelected}
                    onCheckedChange={handleSelectAll}
                    aria-label="Select all"
                  />
                </th>
                <th className="text-left p-3 font-medium">采购单号</th>
                <th className="text-left p-3 font-medium">供应商</th>
                <th className="text-left p-3 font-medium">产品数</th>
                <th className="text-left p-3 font-medium">总金额</th>
                <th className="text-left p-3 font-medium">状态</th>
              </tr>
            </thead>
            <tbody>
              {unassignedPOs.map((po) => {
                const isSelected = selectedPOs.some(p => p._id === po._id);
                return (
                  <tr
                    key={po._id}
                    className={`border-b hover:bg-muted cursor-pointer ${
                      isSelected ? 'bg-primary/10' : ''
                    }`}
                    onClick={() => handlePOSelection(po, !isSelected)}
                  >
                    <td className="p-3">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(checked) => handlePOSelection(po, checked)}
                        aria-label={`Select ${po.purchase_order_id}`}
                      />
                    </td>
                    <td className="p-3">{po.purchase_order_id || po._id}</td>
                    <td className="p-3">{po.supplier?.name || '未知'}</td>
                    <td className="p-3">{(po.products || []).length}</td>
                    <td className="p-3">¥{(po.total_amount || 0).toFixed(2)}</td>
                    <td className="p-3">
                      <Badge variant={po.status === 'confirmed' ? 'default' : 'outline'}>
                        {po.status || 'pending'}
                      </Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {selectedPOs.length > 0 && (
        <Alert className="mt-4 border-green-500 bg-success/10">
          <AlertDescription>
            已选择 {selectedPOs.length} 个采购单
          </AlertDescription>
        </Alert>
      )}
    </div>
  );

  // Step 2: Allocate products
  const renderStep2AllocateProducts = () => (
    <div>
      <p className="text-sm text-muted-foreground mb-4">
        为每个采购单分配产品和数量 (支持部分分配)
      </p>

      {productAllocations.length === 0 ? (
        <Alert variant="destructive" className="mt-4">
          <AlertDescription>
            所选采购单中没有与销售订单匹配的产品
          </AlertDescription>
        </Alert>
      ) : (
        <div className="mt-4 space-y-4">
          {productAllocations.map((poAlloc, poIndex) => (
            <Card key={poAlloc.po_id} className="p-4">
              <h4 className="text-sm font-semibold mb-2">
                {poAlloc.po_number} - {poAlloc.supplier_name}
              </h4>
              <Separator className="my-2" />

              <div className="max-h-[300px] overflow-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted">
                    <tr className="border-b">
                      <th className="text-left p-2 font-medium">产品编号</th>
                      <th className="text-left p-2 font-medium">产品名称</th>
                      <th className="text-right p-2 font-medium">销售需求</th>
                      <th className="text-right p-2 font-medium">采购可用</th>
                      <th className="text-right p-2 font-medium">分配数量</th>
                    </tr>
                  </thead>
                  <tbody>
                    {poAlloc.allocations.map((alloc, productIndex) => (
                      <tr key={`${alloc.so_product_id}-${productIndex}`} className="border-b">
                        <td className="p-2">{alloc.so_product_id}</td>
                        <td className="p-2">{alloc.so_product_name}</td>
                        <td className="p-2 text-right">{alloc.so_required_qty}</td>
                        <td className="p-2 text-right">{alloc.po_available_qty}</td>
                        <td className="p-2 text-right">
                          <Input
                            type="number"
                            value={alloc.allocated_qty}
                            onChange={(e) => handleQuantityChange(poIndex, productIndex, e.target.value)}
                            min={0}
                            max={Math.min(alloc.so_required_qty, alloc.po_available_qty)}
                            step={1}
                            className="w-24 text-right"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  // Step 3: Preview and confirm
  const renderStep3Preview = () => (
    <div>
      <p className="text-sm text-muted-foreground mb-4">
        确认以下关联信息后提交
      </p>

      {previewData && (
        <div className="mt-4">
          <Alert className="mb-4">
            <AlertDescription>
              <span className="font-medium">
                将创建 {previewData.total_mappings} 条产品映射记录，涉及 {previewData.total_pos} 个采购单
              </span>
            </AlertDescription>
          </Alert>

          {previewData.details.map((poAlloc) => (
            <Card key={poAlloc.po_id} className="p-4 mb-4">
              <div className="flex items-center mb-2">
                <CheckCircle className="h-5 w-5 text-success mr-2" />
                <span className="font-medium">
                  {poAlloc.po_number} - {poAlloc.supplier_name}
                </span>
              </div>

              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr className="border-b">
                    <th className="text-left p-2 font-medium">产品编号</th>
                    <th className="text-left p-2 font-medium">产品名称</th>
                    <th className="text-right p-2 font-medium">分配数量</th>
                  </tr>
                </thead>
                <tbody>
                  {poAlloc.allocations
                    .filter(alloc => alloc.allocated_qty > 0)
                    .map((alloc, idx) => (
                      <tr key={idx} className="border-b">
                        <td className="p-2">{alloc.so_product_id}</td>
                        <td className="p-2">{alloc.so_product_name}</td>
                        <td className="p-2 text-right">{alloc.allocated_qty}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  // Render step content
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return renderStep1SelectPOs();
      case 1:
        return renderStep2AllocateProducts();
      case 2:
        return renderStep3Preview();
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl min-h-[600px] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            关联现有采购单到销售订单
          </DialogTitle>
        </DialogHeader>

        <Separator />

        <div className="flex-1 overflow-auto py-4">
          <Stepper />

          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription className="flex items-center justify-between">
                {error}
                <Button variant="ghost" size="sm" onClick={() => setError('')}>
                  关闭
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {renderStepContent()}
        </div>

        <Separator />

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose} disabled={loading || submitting}>
            取消
          </Button>

          {activeStep > 0 && (
            <Button variant="outline" onClick={handleBack} disabled={loading || submitting}>
              上一步
            </Button>
          )}

          {activeStep < steps.length - 1 ? (
            <Button
              onClick={handleNext}
              disabled={loading || (activeStep === 0 && selectedPOs.length === 0)}
            >
              下一步
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <Link2 className="h-4 w-4 mr-2" />
              确认关联
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LinkPODialog;
